import { useCallback, useEffect, useRef, useState } from 'react';

import { showSuccess } from '@components/toaster';
import { Timeout } from '@utils/types';

type UseClipboardOptions = {
  // How long should (in ms) the copied flag stay on after copy
  // Defaults to 3000 ms
  delay?: number;
};

export function useClipboard(options?: UseClipboardOptions) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<Timeout>();

  const timeout = options?.delay ?? 3000;

  const onCopy = useCallback(
    async (text: string, successMessage?: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        if (successMessage) {
          showSuccess(successMessage);
        }
        timeoutRef.current = setTimeout(() => {
          setCopied(false);
        }, timeout);
      } catch (e) {
        // The "clipboard-write" permission of the Permissions API,
        // is granted automatically to pages when they are in the active tab.
        // https://developer.mozilla.org/en-US/docs/Web/API/Clipboard/writeText
        // So we SHOULDN'T have permission errors here but adding the try/catch
        // anyway just to be safe.
      }
    },
    [timeout]
  );

  useEffect(() => {
    return () => {
      window.clearTimeout(timeoutRef.current);
    };
  }, []);

  return { onCopy, copied };
}
